import { HelpLinesSystem, SaveVpcOptions } from '@inter-ikea-storageone/range';
import { reportSaveDesign } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { useScreenshot } from 'contexts/screenshot';
import { useServiceSettings } from 'contexts/serviceSettings';
import { getProductEntities } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { getAgeGroup } from 'ducks/layout';
import { getMarketProducts, getShoppingProducts } from 'ducks/products';
import { getVpcCode, saveVpcCode } from 'ducks/vpc';
import reportError from 'error-reporting/reportError';
import { useSelector } from 'react-redux';
import { isProduction } from 'utils/platform';
import { getQuery, rangeIsSmastad } from 'utils/queryUtils';

export const useVpc = () => {
  const productEntitiesInScene = useSelector(getProductEntities);
  const { captureVPCImages } = useScreenshot();
  const coreApi = useCore();
  const currentProppingAgeGroup = useSelector(getAgeGroup);
  const proppingInVpc = getQuery().get('enableProppingInVpc') === 'true';
  const vpcCode = useSelector(getVpcCode);
  const shoppingProducts = useSelector(getShoppingProducts());
  const { kompisSettings } = useServiceSettings();
  const allProducts = useSelector(getMarketProducts);
  const dispatch = useAppDispatch();

  type SaveVpcProps = {
    doNotTakeScreenshots?: boolean;
    userTriggered?: boolean;
  };

  const save = async (props?: SaveVpcProps) => {
    const vpcImages = props?.doNotTakeScreenshots ? [] : await captureVPCImages();
    const helpLinesSystem = coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem');
    const options: SaveVpcOptions = {
      wallShape:
        helpLinesSystem && helpLinesSystem.visible
          ? {
              dimensions: helpLinesSystem?.getShapeDimensions(),
              type: helpLinesSystem?.currentShapeType
            }
          : undefined,
      proppingAgeGroup: rangeIsSmastad() ? currentProppingAgeGroup : undefined,
      saveProppingEntities: proppingInVpc ? true : false
    };

    if (!vpcCode && productEntitiesInScene.length > 0) {
      const shoppingItems = shoppingProducts.map(p => p.shoppingItem);

      // There is an issue with the shopping items not being populated correctly in some cases during save.
      // This is a temporary error report to Senty to help us track down the issue.
      if (!shoppingItems || shoppingItems.length === 0) {
        reportError(new Error('No shopping items during save'), {
          productEntitiesInScene: productEntitiesInScene
        });
      }

      const configuration = await coreApi.saveVpc(
        kompisSettings,
        shoppingItems,
        vpcImages,
        allProducts,
        isProduction(),
        options
      );
      if (configuration) {
        dispatch(saveVpcCode(configuration.configurationId));
        if (props?.userTriggered) {
          // We only want to send this when a user has manually triggered a save
          reportSaveDesign({ vpcCode: configuration.configurationId });
        }
        if (!configuration.icfSaved) {
          reportError(new Error('Insights Combination Format was not saved successfully.'), {
            vpcCode: configuration.configurationId
          });
        }
      }

      return { vpcImages, vpcCode: configuration?.configurationId ?? null };
    }

    return { vpcImages, vpcCode };
  };

  return { save };
};
